import dayjs from "dayjs";
import throttle from "lodash.throttle";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Line, LineChart, ResponsiveContainer, YAxis } from "recharts";
import { useGetHistoryChartLite } from "./hooks/ueGetHistoryChartLite";
type ChartPoint = { time: string; value: number };

interface SparklineChartProps {
  dataChart: ChartPoint[];
  color: string;
}

const SparklineChart = memo(
  ({ dataChart, color }: SparklineChartProps) => {
    // tính min/max để scale theo biến động
    const min = Math.min(...dataChart.map((d) => d.value));
    const max = Math.max(...dataChart.map((d) => d.value));

    return (
      <ResponsiveContainer width="100%" height={40}>
        <LineChart data={dataChart}>
          {/* scale Y theo min/max để không bị "flatten" */}
          <YAxis hide domain={[min * 0.999, max * 1.001]} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={true}
            animationDuration={500}
            animationEasing="ease-in-out"
          />
        </LineChart>
      </ResponsiveContainer>
    );
  },
  (prev, next) => {
    if (prev.dataChart !== next.dataChart) return false;
    if (prev.color !== next.color) return false;
    return true;
  }
);

type PropsChartView = {
  symbol: string;
  historyCall: string;
  rawPrice24h: number | string | undefined;
};

function ChartView({ symbol, historyCall, rawPrice24h }: PropsChartView) {
  const [throttledPrice24h, setThrottledPrice24h] = useState<number | null>(null);

  const { data } = useGetHistoryChartLite(symbol, historyCall, "1d");

  const dataChart = useMemo(() => {
    return data?.slice(-200)?.map((item) => ({
      time: dayjs(item.time * 1000).format(),
      value: item.value,
    }));
  }, [data]);

  const throttledUpdate = useRef(
    throttle((newPrice: number) => {
      setThrottledPrice24h(newPrice);
    }, 1000)
  ).current;

  useEffect(() => {
    if (rawPrice24h != null) {
      throttledUpdate(Number(rawPrice24h));
    }
  }, [rawPrice24h, throttledUpdate]);

  const textColor = useMemo(() => {
    return throttledPrice24h != null && throttledPrice24h >= 0 ? "#38F24A" : "#FF695F";
  }, [throttledPrice24h]);

  return <SparklineChart dataChart={dataChart ?? []} color={textColor} />;
}

export default memo(ChartView);
